<template>
  <div class="intro-paragraph-selector">
    <label class="field-label">{{ label }}</label>
    
    <!-- Dropdown with custom items -->
    <v-select
      v-model="selectedParagraphId"
      :items="paragraphItems"
      variant="outlined"
      density="compact"
      hide-details
      item-title="displayTitle"
      item-value="id"
      class="intro-paragraph-select"
      @update:modelValue="onSelectionChange"
    >
      <!-- Custom item template with edit icons -->
      <template v-slot:item="{ item, props }">
        <v-list-item v-bind="props" class="paragraph-item">
          <template v-slot:append v-if="item.raw.isCustom">
            <div class="item-actions">
              <v-btn
                size="small"
                variant="text"
                color="primary"
                @click.stop="editParagraph(item.raw)"
                class="edit-btn"
              >
                <img src="/assets/edit.png" alt="Edit" class="edit-icon" />
              </v-btn>
            </div>
          </template>
        </v-list-item>
      </template>

      <!-- Fixed "Add Custom" button at bottom -->
      <template v-slot:append-item>
        <v-list-item
          class="add-custom-item"
          @click="openCreateDialog"
        >
          <template v-slot:prepend>
            <v-icon color="#000000">mdi-plus</v-icon>
          </template>
          <v-list-item-title class="add-custom-text">Add Custom</v-list-item-title>
        </v-list-item>
      </template>
    </v-select>

    <!-- Create/Edit Dialog -->
    <BaseDialog
      :visible="showCreateDialog"
      :config="createDialogConfig"
      :isValidForSubmit="isValid"
      @close="closeCreateDialog"
      @action="saveParagraph"
    >
      <p class="text-subtitle-2 mb-4">
        {{ dialogDescription }}
      </p>

      <!-- Intro Name Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">{{ nameLabel }}</label>
        <v-text-field
          v-model="dialogData.name"
          placeholder="List Name"
          variant="outlined"
          density="comfortable"
          hide-details="auto"
        ></v-text-field>
        <div class="field-hint" :class="{ 'error': dialogData.name && dialogData.name.length > 20 }">
          {{ dialogData.name ? dialogData.name.length : 0 }}/20 characters
        </div>
      </div>

      <!-- Intro Content Field -->
      <div class="dialog-field-container">
        <label class="dialog-field-label">{{ contentLabel }}</label>
        <div class="intro-content-container">
          <VueEditor
            ref="introContentEditor"
            key="introContentEditor"
            class="intro-content-vue-editor"
            v-model="dialogData.content"
            :editorToolbar="customToolbar"
            :editorOptions="editorOptions"
            placeholder="Additional Comments"
            @input="onIntroContentInput"
            @text-change="onIntroContentTextChange"
          />
          
          <!-- Character Count -->
          <div class="intro-content-char-count" :class="{ error: introContentLength > 200 }">
            {{ introContentLength }}/200 characters
          </div>
        </div>
      </div>

      <!-- Delete button for editing (placed in content area) -->
      <div v-if="isEditing" class="delete-section">
        <v-btn
          variant="outlined"
          color="#003f2d"
          @click="deleteCurrentParagraph"
          :loading="isDeleting"
          class="delete"
        >
          <img src="/assets/delete.png" alt="Delete" class="delete-icon" />
          Delete
        </v-btn>
      </div>
    </BaseDialog>

    <!-- Delete Confirmation Dialog -->
    <BaseDialog
      :visible="showDeleteDialog"
      :config="deleteDialogConfig"
      :isValidForSubmit="true"
      @close="showDeleteDialog = false"
      @action="confirmDelete"
    >
      <p class="text-body-1 mb-3">
        Are you sure you want to delete "<strong>{{ paragraphToDelete?.name }}</strong>"?
      </p>
      <p class="text-body-2 text-medium-emphasis">
        This action cannot be undone.
      </p>
    </BaseDialog>
  </div>
</template>

<script>
import BaseDialog from '../commons/BaseDialog.vue';
import { VueEditor } from 'vue3-editor';
import {
  initializeIntroParagraphService,
  getAllIntroParagraphs,
  createCustomIntroParagraph,
  updateCustomIntroParagraph,
  deleteCustomIntroParagraph,
  getSelectedParagraphId,
  setSelectedParagraphId,
  getWordCount
} from '../../service/introParagraphService.js';

export default {
  name: 'IntroParagraphSelector',
  
  components: {
    BaseDialog,
    VueEditor
  },
  
  props: {
    modelValue: {
      type: String,
      default: 'default'
    },
    label: {
      type: String
    },
    dialogTitle: {
      type: String
    },
    dialogDescription: {
      type: String
    },
    nameLabel: {
      type: String
    },
    contentLabel: {
      type: String
    },
    storageContext: {
      type: String,
      default: 'propertyContact',
      validator: (value) => ['propertyContact', 'clientIntroduction'].includes(value)
    }
  },

  emits: ['update:modelValue', 'paragraph-changed'],

  data() {
    return {
      selectedParagraphId: 'default',
      allParagraphs: [],
      showCreateDialog: false,
      showDeleteDialog: false,
      isEditing: false,
      isLoading: false,
      isSaving: false,
      isDeleting: false,
      
      dialogData: {
        id: null,
        name: '',
        content: ''
      },
      
      dialogErrors: {
        name: [],
        content: []
      },
      
      paragraphToDelete: null,
      wordCount: 0,
      
      // VueEditor configuration
      customToolbar: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ],
      
      editorOptions: {
        debug: false,
        modules: {
          toolbar: [
            ['bold', 'italic', 'underline'],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link'],
            ['clean']
          ],
          history: {
            delay: 1000,
            maxStack: 50,
            userOnly: false
          },
          clipboard: {
            matchVisual: false
          }
        },
        theme: 'snow',
        strict: false,
        scrollingContainer: null,
        bounds: null,
        readOnly: false,
      }
    };
  },

  computed: {
    paragraphItems() {
      return this.allParagraphs.map(paragraph => ({
        ...paragraph,
        displayTitle: paragraph.name,
        // Add visual indicator for custom paragraphs
        title: paragraph.isCustom ? `${paragraph.name} (Custom)` : paragraph.name
      }));
    },

    isValid() {
      // Ensure we have the dialog data
      if (!this.dialogData) return false;
      
      // Check name: must have actual text and not exceed length
      const nameText = String(this.dialogData.name || '').trim();
      if (!nameText || nameText.length > 20) return false;
      
      // Check content: must have actual text content (not just HTML markup) and not exceed limit
      const contentLength = this.introContentLength;
      if (contentLength <= 0 || contentLength > 200) return false;
      
      return true;
    },

    introContentLength() {
      if (!this.dialogData.content) return 0;
      
      try {
        // Get plain text length (strip HTML tags)
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = this.dialogData.content || '';
        const textContent = (tempDiv.textContent || tempDiv.innerText || '').trim();
        return textContent.length;
      } catch (error) {
        console.warn('Error calculating intro content length:', error);
        return 0;
      }
    },

    createDialogConfig() {
      return {
        title: this.dialogTitle,
        label: this.isEditing ? 'Save Changes' : 'Add Intro',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false
      };
    },

    deleteDialogConfig() {
      return {
        title: 'Confirm Delete',
        label: 'Delete',
        needCanacel: true,
        hasIcon: false,
        isSelectAssignment: false,
        isValidForSubmit: true
      };
    }
  },

  async mounted() {
    await this.initializeComponent();
  },

    watch: {
      modelValue: {
        immediate: true,
        handler(newValue) {
          if (newValue && newValue !== this.selectedParagraphId) {
            this.selectedParagraphId = newValue;
          }
        }
      }
    },

  methods: {
    async initializeComponent() {
      try {
        this.isLoading = true;
        
        // Initialize the service
        await initializeIntroParagraphService();
        
        // Load all paragraphs
        await this.loadParagraphs();
        
        // Check if parent has already set a value (e.g., 'default' during reset)
        if (this.modelValue === 'default') {
          this.selectedParagraphId = 'default';
          // Emit the default paragraph object
          const defaultParagraph = this.allParagraphs.find(p => p.id === 'default');
          if (defaultParagraph) {
            this.$emit('paragraph-changed', defaultParagraph);
          }
        } else {
          // Load saved selection only if parent hasn't explicitly set to default
          const selectedId = await getSelectedParagraphId(this.storageContext);
          this.selectedParagraphId = selectedId || 'default';
          
          // Emit initial value
          this.$emit('update:modelValue', this.selectedParagraphId);
          
          // Emit the selected paragraph object
          const selectedParagraph = this.allParagraphs.find(p => p.id === this.selectedParagraphId);
          if (selectedParagraph) {
            this.$emit('paragraph-changed', selectedParagraph);
          }
        }
        
      } catch (error) {
        console.error('Error initializing intro paragraph selector:', error);
        this.$emit('error', 'Failed to load intro paragraphs');
      } finally {
        this.isLoading = false;
      }
    },

    async loadParagraphs() {
      try {
        this.allParagraphs = await getAllIntroParagraphs(this.storageContext);
        console.log('📋 Loaded intro paragraphs:', this.allParagraphs.length);
      } catch (error) {
        console.error('Error loading paragraphs:', error);
        this.allParagraphs = [];
      }
    },

    async onSelectionChange(paragraphId) {
      try {
        if (paragraphId) {
          // Save selection
          await setSelectedParagraphId(paragraphId, this.storageContext);
          
          // Emit changes
          this.$emit('update:modelValue', paragraphId);
          
          // Find the selected paragraph and emit it
          const selectedParagraph = this.allParagraphs.find(p => p.id === paragraphId);
          if (selectedParagraph) {
            this.$emit('paragraph-changed', selectedParagraph);
          }
          
          console.log('✅ Selected intro paragraph:', paragraphId);
        }
      } catch (error) {
        console.error('Error changing paragraph selection:', error);
      }
    },

    openCreateDialog() {
      this.isEditing = false;
      this.dialogData = {
        id: null,
        name: '',
        content: ''
      };
      this.clearErrors();
      this.updateWordCount();
      this.showCreateDialog = true;
    },

    editParagraph(paragraph) {
      this.isEditing = true;
      this.dialogData = {
        id: paragraph.id,
        name: paragraph.name,
        content: paragraph.content
      };
      this.clearErrors();
      this.updateWordCount();
      this.showCreateDialog = true;
    },

    deleteParagraph(paragraph) {
      this.paragraphToDelete = paragraph;
      this.showDeleteDialog = true;
    },

    closeCreateDialog() {
      this.showCreateDialog = false;
      this.dialogData = { id: null, name: '', content: '' };
      this.clearErrors();
      this.wordCount = 0;
    },

    clearErrors() {
      this.dialogErrors = {
        name: [],
        content: []
      };
    },

    // Clear specific field errors
    clearFieldError(fieldName) {
      if (this.dialogErrors[fieldName]) {
        this.dialogErrors[fieldName] = [];
      }
    },

    updateWordCount() {
      this.wordCount = getWordCount(this.dialogData.content);
    },

    onIntroContentInput(content) {
      try {
        this.dialogData.content = content || '';
      } catch (error) {
        console.warn('Error in intro content input handler:', error.message);
        if (content) {
          this.dialogData.content = content;
        }
      }
    },

    onIntroContentTextChange() {
      try {
        // This method is called when the editor content changes
        // The content is automatically bound to dialogData.content via v-model
        this.updateWordCount();
      } catch (error) {
        console.warn('Error in intro content text change handler:', error.message);
      }
    },

    async saveParagraph() {
      try {
        this.isSaving = true;

        let savedParagraph;
        if (this.isEditing) {
          // Update existing paragraph
          savedParagraph = await updateCustomIntroParagraph(
            this.dialogData.id,
            this.dialogData.name,
            this.dialogData.content,
            this.storageContext
          );
          console.log('✅ Updated paragraph:', savedParagraph.name);
        } else {
          // Create new paragraph
          savedParagraph = await createCustomIntroParagraph(
            this.dialogData.name,
            this.dialogData.content,
            this.storageContext
          );
          console.log('✅ Created new paragraph:', savedParagraph.name);
        }

        // Reload paragraphs and select the new/updated one
        await this.loadParagraphs();
        await this.onSelectionChange(savedParagraph.id);

        this.closeCreateDialog();
        this.$emit('success', `Intro paragraph ${this.isEditing ? 'updated' : 'created'} successfully`);

      } catch (error) {
        console.error('Error saving paragraph:', error);
        this.$emit('error', error.message || 'Failed to save intro paragraph');
      } finally {
        this.isSaving = false;
      }
    },

    deleteCurrentParagraph() {
      if (this.dialogData.id) {
        this.paragraphToDelete = this.allParagraphs.find(p => p.id === this.dialogData.id);
        this.showDeleteDialog = true;
      }
    },

    async confirmDelete() {
      try {
        if (!this.paragraphToDelete) return;

        this.isDeleting = true;

        await deleteCustomIntroParagraph(this.paragraphToDelete.id, this.storageContext);
        console.log('✅ Deleted paragraph:', this.paragraphToDelete.name);

        // Reload paragraphs
        await this.loadParagraphs();

        // Reset selection to default after deletion
        this.selectedParagraphId = 'default';
        await setSelectedParagraphId('default', this.storageContext);
        this.$emit('update:modelValue', 'default');
        this.$emit('paragraph-changed', null);

        // Close dialogs
        this.showDeleteDialog = false;
        this.showCreateDialog = false;

        // Reset dialog data
        this.dialogData = { id: null, name: '', content: '' };
        this.paragraphToDelete = null;

        this.$emit('success', 'Intro paragraph deleted successfully');

      } catch (error) {
        console.error('Error deleting paragraph:', error);
        this.$emit('error', error.message || 'Failed to delete intro paragraph');
      } finally {
        this.isDeleting = false;
      }
    }
  }
};
</script>

<style scoped>
/* Component-specific styles */
.intro-paragraph-selector {
  margin-bottom: 1rem;
}

/* Ensure the select dropdown has exact height with no gaps */
.intro-paragraph-select :deep(.v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
  min-height: 300px !important;
  overflow: hidden !important;
  padding: 0 !important;
  margin: 0 !important;
  border: 1px solid #ccc;
}

/* Force exact container dimensions */
.intro-paragraph-select :deep(.v-menu > .v-overlay__content) {
  height: 300px !important;
  max-height: 300px !important;
}

/* Create exact container structure with no gaps */
.intro-paragraph-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

.intro-paragraph-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Style the list container with exact scrollable area */
.intro-paragraph-select :deep(.v-list) {
  padding: 0 !important;
  margin: 0 !important;
  overflow-y: auto !important;
  flex: 1 !important;
  height: calc(300px - 60px) !important;
  max-height: calc(300px - 60px) !important;
  min-height: calc(300px - 60px) !important;
}

/* Ensure list items have no gaps */
.intro-paragraph-select :deep(.v-list-item) {
  padding: 8px 16px !important;
  margin: 0 !important;
  border-bottom: 1px solid #f0f0f0;
}

.field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 16px;
}

.paragraph-item {
  position: relative;
}

.item-actions {
  display: flex;
  gap: 4px;
  opacity: 0;
  transition: opacity 0.2s;
}

.paragraph-item:hover .item-actions {
  opacity: 1;
}



.edit-icon {
  width: 26px;
  height: 26px;
  object-fit: contain;
}

.add-custom-item {
  position: sticky !important;
  bottom: 0 !important;
  left: 0 !important;
  right: 0 !important;
  z-index: 10 !important;
  gap: 32px;
  background-color: white !important;
  min-height: 50px !important;
  height: 50px !important;
  padding: 16px !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
  width: 100% !important;
}

/* Remove the ::after pseudo-element as it's no longer needed */
.add-custom-item::before {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 0;
  right: 0;
  height: 10px;
  background-color: white;
  z-index: 11;
}

.add-custom-text {
  color: #000000;
}

.add-custom-item:hover {
  background-color: #e8f5e8 !important;
}

/* Dialog content styles */
.word-count-info {
  text-align: right;
  margin-top: -8px;
  margin-bottom: 16px;
}

/* Dropdown styles - Fix entire container to prevent text bleeding */
.intro-paragraph-select :deep(.v-list) {
  max-height: 300px;
  overflow-y: auto;
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  position: relative;
}

/* Fix the overall dropdown menu container */
.intro-paragraph-select :deep(.v-menu > .v-overlay__content > .v-card) {
  overflow: hidden !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Ensure the select content area is properly contained */
.intro-paragraph-select :deep(.v-select__content) {
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Fix the list container to prevent overflow */
.intro-paragraph-select :deep(.v-list) {
  flex: 1 1 auto !important;
  overflow-y: auto !important;
  max-height: 240px !important; /* Leave 60px for Add Custom button */
}

/* Ensure the append item area is properly positioned */
.intro-paragraph-select :deep(.v-list-group) {
  margin-bottom: 0 !important;
}

/* Ensure Add Custom button stays at bottom and fills remaining space */
.intro-paragraph-select :deep(.v-list-item--append-item) {
  position: sticky;
  bottom: 0;
  background: white !important;
  z-index: 4;
  min-height: 60px !important;
  height: 60px !important;
  padding: 16px !important;
  margin-bottom: 0 !important;
  border-top: 2px solid #e0e0e0;
  box-shadow: 0 -4px 12px rgba(0, 0, 0, 0.15);
  display: flex !important;
  align-items: center !important;
}

/* Remove any padding/margin from the dropdown content container */
.intro-paragraph-select :deep(.v-select__content) {
  overflow: hidden !important;
  padding-bottom: 0 !important;
  max-height: 300px !important;
  display: flex !important;
  flex-direction: column !important;
}

.intro-paragraph-select :deep(.v-select__content .v-list) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
  flex: 1 1 auto !important;
  max-height: 240px !important;
}

/* Ensure the dropdown menu itself has no bottom spacing and proper height */
.intro-paragraph-select :deep(.v-overlay__content) {
  padding-bottom: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Fix the Vuetify menu wrapper */
.intro-paragraph-select :deep(.v-menu > .v-overlay__content) {
  overflow: hidden !important;
  max-height: 300px !important;
}

/* Ensure proper container for the entire select */
.intro-paragraph-select :deep(.v-field) {
  position: relative;
}

/* Fix any default Vuetify card padding in dropdown */
.intro-paragraph-select :deep(.v-card) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
  display: flex !important;
  flex-direction: column !important;
}

/* Remove all default padding from v-select components */
.intro-paragraph-select :deep(.v-select .v-menu) {
  padding: 0 !important;
}

.intro-paragraph-select :deep(.v-menu .v-list) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Target the specific menu content wrapper */
.intro-paragraph-select :deep(.v-menu__content) {
  padding: 0 !important;
  margin: 0 !important;
  max-height: 300px !important;
  overflow: hidden !important;
}

/* Remove padding from overlay scrim */
.intro-paragraph-select :deep(.v-overlay__scrim) {
  padding: 0 !important;
}

/* Ensure list group has no spacing */
.intro-paragraph-select :deep(.v-list-group__items) {
  padding: 0 !important;
}

/* Remove any theme-specific padding */
.intro-paragraph-select :deep(.v-theme--light) {
  padding: 0 !important;
}

/* Force the entire dropdown menu to have no spacing */
.intro-paragraph-select :deep(*) {
  box-sizing: border-box;
}

.intro-paragraph-select :deep(.v-menu),
.intro-paragraph-select :deep(.v-overlay),
.intro-paragraph-select :deep(.v-overlay__content),
.intro-paragraph-select :deep(.v-card),
.intro-paragraph-select :deep(.v-list),
.intro-paragraph-select :deep(.v-select__content) {
  padding: 0 !important;
  margin: 0 !important;
}

/* Ensure list items don't bleed through */
.intro-paragraph-select :deep(.v-list-item) {
  position: relative;
  z-index: 1;
}

/* Force remove any default Vuetify spacing */
.intro-paragraph-select :deep(.v-list),
.intro-paragraph-select :deep(.v-list-item-group),
.intro-paragraph-select :deep(.v-menu__content),
.intro-paragraph-select :deep(.v-select__content) {
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}

/* Ensure the entire dropdown area has no gaps */
.intro-paragraph-select :deep(.v-field__input) {
  padding-bottom: 0 !important;
}

::v-deep .v-list-item__prepend {
  width: 0px !important;
}

/* Dialog field styling */
.dialog-field-container {
  margin-bottom: 1rem;
}

.dialog-field-label {
  display: block;
  margin-bottom: 8px;
  color: #000;
  font-size: 14px;
  font-weight: 500;
}

.field-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  text-align: right;
}

.field-hint.error {
  color: #d32f2f;
  font-weight: 500;
}

/* Delete button styling */
.delete-section {
  margin-top: 1rem;
  margin-bottom: 1rem;
}

.delete-icon {
  width: 16px;
  height: 16px;
  margin-right: 8px;
  object-fit: contain;
}
.delete {
  padding: 4px 8px 4px 8px;
  text-transform: capitalize;
}

/* VueEditor styling for intro content */
.intro-content-container {
  position: relative;
}

.intro-content-vue-editor {
  min-height: 150px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.intro-content-vue-editor ::v-deep .ql-editor {
  min-height: 120px;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.4;
}

.intro-content-vue-editor ::v-deep .ql-toolbar {
  border-bottom: 1px solid #e0e0e0;
  font-family: inherit;
}

.intro-content-char-count {
  position: absolute;
  bottom: -20px;
  right: 0;
  font-size: 12px;
  color: #666;
  margin-top: 4px;
}

.intro-content-char-count.error {
  color: #d32f2f;
  font-weight: 500;
}

.error-message {
  color: #d32f2f;
  font-size: 12px;
  margin-top: 4px;
}
</style>